import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Button,
  TextField as MuiTextField,
} from "@mui/material";
import { spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";
import { useState } from "react";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

function ResetPasswordForm({ stageCode }) {
  const [stage, setStage] = useState(stageCode); // 1 = email stage, 2 = code stage
  const navigate = useNavigate();
  const { resetPassword, confirmPassword } = useAuth();

  return (
    <>
      {stage === 1 && (
        <div>
          <Formik
            initialValues={{
              email: "",
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email("Must be a valid email")
                .max(255)
                .required("Email is required"),
            })}
            onSubmit={async (
              values,
              { setErrors, setStatus, setSubmitting }
            ) => {
              try {
                resetPassword(values.email);
                setStage(2);
                //navigate("/auth/sign-in");
              } catch (error) {
                const message = error.message || "Something went wrong";

                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                {errors.submit && (
                  <Alert mt={2} mb={1} severity="warning">
                    {errors.submit}
                  </Alert>
                )}
                <TextField
                  type="email"
                  name="email"
                  label="Email Address"
                  value={values.email}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  autoComplete="username"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={3}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Reset Password
                </Button>
              </form>
            )}
          </Formik>
          <Button
            onClick={() => {
              setStage(2);
            }}
            fullWidth
            color="primary"
          >
            I have already received my verification Code
          </Button>
        </div>
      )}

      {stage === 2 && (
        <Formik
          initialValues={{
            email: "",
            code: "",
            password: "",
            confirmedPassword: "",
            submit: false,
          }}
          validationSchema={Yup.object().shape({
            code: Yup.string()
              .min(5, "Must be at least 5 characters")
              .required("Required"),
            email: Yup.string()
              .email("Must be a valid email")
              .max(255)
              .required("Email is required"),
            password: Yup.string()
              .min(8, "Must be at least 8 characters")
              .max(255)
              .matches(
                /^(?=.*[a-z])/,
                "Must contain at least one lowercase character"
              )
              .matches(
                /^(?=.*[A-Z])/,
                "Must contain at least one uppercase character"
              )
              .matches(/^(?=.*[0-9])/, "Must contain at least one number")
              .matches(
                /^(?=.*[!@#$%^&*])/,
                "Must contain at least one special character from ! @ # $ % ^ & * "
              )
              .required("Required"),
            confirmedPassword: Yup.string().when("password", {
              is: (val) => (val && val.length > 0 ? true : false),
              then: Yup.string().oneOf(
                [Yup.ref("password")],
                "Both password need to be the same"
              ),
            }),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              confirmPassword(values.email, values.code, values.password);
              navigate("/auth/sign-in");
            } catch (error) {
              const message = error.message || "Something went wrong";

              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert mt={2} mb={1} severity="warning">
                  {errors.submit}
                </Alert>
              )}
              <TextField
                type="code"
                name="code"
                label="Verification Code"
                value={values.code}
                error={Boolean(touched.code && errors.code)}
                fullWidth
                helperText={touched.code && errors.code}
                onBlur={handleBlur}
                onChange={handleChange}
                my={3}
              />
              <TextField
                type="email"
                name="email"
                label="Email Address"
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                autoComplete="username"
                onBlur={handleBlur}
                onChange={handleChange}
                my={3}
              />
              <TextField
                name="password"
                label="Password"
                value={values.password}
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                autoComplete="new-password"
                variant="outlined"
                my={2}
              />

              <TextField
                name="confirmedPassword"
                label="Confirm password"
                value={values.confirmedPassword}
                error={Boolean(
                  touched.confirmedPassword && errors.confirmedPassword
                )}
                fullWidth
                helperText={
                  touched.confirmedPassword && errors.confirmedPassword
                }
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                autoComplete="new-password"
                variant="outlined"
                my={2}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                Set New Password
              </Button>
            </form>
          )}
        </Formik>
      )}
    </>
  );
}

export default ResetPasswordForm;
